// public/js/features/assets/slot-derived-editor.js
// "Derived sensors" section on the asset detail page — configured PER SENSOR SLOT
// (not per asset), so two identical sensors on one asset can carry different (even
// opposite) rules. Each slot picks from the tenant's rule TEMPLATES: a default
// template applies automatically (toggle it off per slot), a plain template is
// applied when selected, either can be overridden (threshold/direction) just for
// that slot, and a slot can add a fully custom inline rule. Persists
// asset.slotRules = { slotId: SlotBinding[] }. Self-gates on manageTenants.
// See docs/derived-sensors.md.
import { updateAsset } from './api.js';
import { loadTenants } from '../../modules/tenants.js';
import { can, getCurrentUser } from '../../modules/rbac.js';
import { SOURCE_METRICS } from '../settings/derived-rule-editor.js';
import { slotDisplayLabel, deviceRole } from './slot-utils.js';

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const metricLabel = (k) => SOURCE_METRICS.find(m => m.key === k)?.label || k || '?';
const sideLabel = (aw) => (aw === 'below' ? 'below' : 'at/above');

// The effective rule for a template + a slot binding (for the read-only preview).
function effective(t, binding) {
  const r = { ...t };
  if (binding?.overrides) for (const k of ['threshold', 'activeWhen', 'hysteresis', 'labelActive', 'labelInactive']) {
    if (binding.overrides[k] !== undefined) r[k] = binding.overrides[k];
  }
  return r;
}

export async function renderAssetDerivedSection(container, asset) {
  if (!container) return;
  const groups = getCurrentUser().groups || [];
  if (!can(groups, 'manageTenants')) { container.innerHTML = ''; return; }

  const assetUUID = asset.assetUUID;
  const slotRules = (asset.slotRules && typeof asset.slotRules === 'object') ? asset.slotRules : {};

  // Tenant templates (defaults + selectable). A tenant admin without listTenants
  // can't load them — then there is nothing to configure here.
  let templates = [];
  try {
    const tenants = await loadTenants();
    const t = tenants.find(x => x.tenantId === asset.tenantId);
    templates = (Array.isArray(t?.sensorRules) ? t.sensorRules : []).filter(r => r && r.ruleId && r.derivedKey);
  } catch { templates = []; }

  // Slots that carry a device (derived rules apply to any device's metrics).
  const slots = (Array.isArray(asset.deviceSlots) ? asset.deviceSlots : []).filter(s => s && s.slotId);
  const deviceBySlot = new Map((asset.assignedDevices || []).map(d => [d.slotId, d]));

  const activeCount = Object.values(slotRules).reduce((n, b) => n + (Array.isArray(b) ? b.length : 0), 0);
  const headBadge = (n) => n > 0
    ? `<span class="badge text-bg-primary"><i class="fa-solid fa-sliders me-1"></i>${n} per-sensor rule${n === 1 ? '' : 's'}</span>`
    : `<span class="badge bg-light text-secondary">tenant defaults</span>`;

  if (!templates.length) {
    // Nothing to bind to yet — point the admin at the template library.
    container.innerHTML = `
      <div class="card shadow-sm">
        <div class="card-header py-2 fw-semibold"><i class="fa-solid fa-wave-square me-2"></i>Derived sensors</div>
        <div class="card-body text-muted small">No derived-sensor templates in this tenant yet. Create them under
          <b>Settings → Derived sensors</b>; then apply them per sensor here.</div>
      </div>`;
    return;
  }

  // One template row for one slot.
  const templateRow = (slot, t) => {
    const bindings = Array.isArray(slotRules[slot.slotId]) ? slotRules[slot.slotId] : [];
    const binding = bindings.find(b => b.ruleId === t.ruleId);
    const disabled = !!binding?.disabled;
    const applied = binding ? !disabled : !!t.isDefault;       // default applies unless disabled
    const ov = (binding && binding.overrides) || {};
    const hasOv = ov.threshold !== undefined || ov.activeWhen !== undefined;
    const eff = effective(t, binding);
    return `
      <div class="d-flex flex-column gap-1 py-2 border-top sde-tpl" data-rule-id="${esc(t.ruleId)}">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <div class="form-check form-switch m-0">
            <input class="form-check-input sde-apply" type="checkbox" role="switch" ${applied ? 'checked' : ''}>
          </div>
          <span class="fw-semibold">${esc(t.label || t.derivedKey)}</span>
          <code class="text-muted small">${esc(t.derivedKey)}</code>
          ${t.isDefault ? '<span class="badge rounded-pill text-bg-primary" style="font-size:.6rem;">Default</span>' : '<span class="badge rounded-pill text-bg-light text-dark border" style="font-size:.6rem;">Template</span>'}
          <span class="text-muted small">· ${esc(metricLabel(t.sourceMetric))} ${sideLabel(eff.activeWhen)} ${esc(eff.threshold)}</span>
          <button type="button" class="btn btn-sm btn-link p-0 ms-auto sde-ovtoggle ${applied ? '' : 'd-none'}">${hasOv ? 'Edit override' : 'Override for this sensor'}</button>
        </div>
        <div class="row g-2 sde-ov ${applied && hasOv ? '' : 'd-none'}" style="max-width:520px;">
          <div class="col-6">
            <label class="form-label small mb-0">Threshold (blank = ${esc(t.threshold)})</label>
            <input type="number" step="any" class="form-control form-control-sm sde-ov-threshold" value="${ov.threshold !== undefined ? esc(ov.threshold) : ''}" placeholder="${esc(t.threshold)}">
          </div>
          <div class="col-6">
            <label class="form-label small mb-0">Active (1) when</label>
            <select class="form-select form-select-sm sde-ov-active">
              <option value="">(template: ${sideLabel(t.activeWhen)})</option>
              <option value="atOrAbove" ${ov.activeWhen === 'atOrAbove' ? 'selected' : ''}>at or above</option>
              <option value="below" ${ov.activeWhen === 'below' ? 'selected' : ''}>below</option>
            </select>
          </div>
        </div>
      </div>`;
  };

  const slotCard = (slot) => {
    const dev = deviceBySlot.get(slot.slotId);
    const name = slotDisplayLabel(slot, dev);
    const role = slot.role || deviceRole(dev);
    const inlineBindings = (Array.isArray(slotRules[slot.slotId]) ? slotRules[slot.slotId] : []).filter(b => b.inline);
    return `
      <div class="border rounded p-2 mb-2 sde-slot" data-slot-id="${esc(slot.slotId)}">
        <div class="fw-semibold small mb-1"><i class="fa-solid fa-microchip me-1 text-secondary"></i>${esc(name)}
          <span class="text-muted">${dev ? esc(dev.deviceId) : 'unpaired'}${role ? ' · ' + esc(role) : ''}</span></div>
        <div class="sde-templates">${templates.map(t => templateRow(slot, t)).join('')}</div>
        <div class="sde-inline mt-1">${inlineBindings.map(b => inlineRow(b.inline)).join('')}</div>
        <button type="button" class="btn btn-sm btn-outline-secondary mt-2 sde-add-inline"><i class="fa-solid fa-plus me-1"></i>Custom rule for this sensor</button>
      </div>`;
  };

  container.innerHTML = `
    <div class="card shadow-sm sde-card ${activeCount ? 'border-primary' : ''}">
      <div class="card-header py-2 d-flex justify-content-between align-items-center sde-head" role="button">
        <span class="fw-semibold d-flex align-items-center gap-2">
          <i class="fa-solid fa-chevron-right sde-chevron text-secondary"></i>
          <span><i class="fa-solid fa-wave-square me-2"></i>Derived sensors <span class="text-muted small">· per sensor</span></span>
        </span>
        <span class="sde-badge">${headBadge(activeCount)}</span>
      </div>
      <div class="card-body sde-body d-none">
        <p class="text-muted small mb-2">Configure the derived state <b>per paired sensor</b>. A
          <span class="badge rounded-pill text-bg-primary" style="font-size:.6rem;">Default</span> template runs automatically —
          switch it off for a sensor, or override its threshold/direction just for that one. Add a custom rule for a sensor that needs its own.</p>
        ${slots.length ? slots.map(slotCard).join('') : '<div class="text-muted small">No paired sensors on this asset yet.</div>'}
        <div class="d-flex mt-2"><button id="sde-save" class="btn btn-primary btn-sm ms-auto"><i class="fa-solid fa-check me-1"></i>Save sensor rules</button></div>
      </div>
    </div>`;

  const body = container.querySelector('.sde-body');
  container.querySelector('.sde-head')?.addEventListener('click', () => {
    const collapsed = body.classList.toggle('d-none');
    const chev = container.querySelector('.sde-chevron');
    chev.classList.toggle('fa-chevron-right', collapsed);
    chev.classList.toggle('fa-chevron-down', !collapsed);
  });

  // Wire template rows: apply toggle shows/hides the override button; override
  // button reveals the fields.
  const wireSlot = (slotEl) => {
    slotEl.querySelectorAll('.sde-tpl').forEach(row => {
      const apply = row.querySelector('.sde-apply');
      const ovBtn = row.querySelector('.sde-ovtoggle');
      const ovBox = row.querySelector('.sde-ov');
      apply?.addEventListener('change', () => {
        ovBtn?.classList.toggle('d-none', !apply.checked);
        if (!apply.checked) ovBox?.classList.add('d-none');
      });
      ovBtn?.addEventListener('click', () => ovBox?.classList.toggle('d-none'));
    });
    slotEl.querySelector('.sde-add-inline')?.addEventListener('click', () => {
      const box = slotEl.querySelector('.sde-inline');
      const tmp = document.createElement('div');
      tmp.innerHTML = inlineRow({});
      box.appendChild(tmp.firstElementChild);
    });
  };
  container.querySelectorAll('.sde-slot').forEach(wireSlot);
  // Inline delete (delegated).
  container.addEventListener('click', (e) => {
    const del = e.target.closest('.sde-inline-del');
    if (del) del.closest('.sde-inline-row')?.remove();
  });

  container.querySelector('#sde-save')?.addEventListener('click', async () => {
    const btn = container.querySelector('#sde-save');
    const built = {};
    let error = null;
    container.querySelectorAll('.sde-slot').forEach(slotEl => {
      const slotId = slotEl.dataset.slotId;
      const bindings = [];
      slotEl.querySelectorAll('.sde-tpl').forEach(row => {
        const ruleId = row.dataset.ruleId;
        const t = templates.find(x => x.ruleId === ruleId);
        const applied = row.querySelector('.sde-apply').checked;
        if (!applied) { if (t?.isDefault) bindings.push({ ruleId, disabled: true }); return; }
        // applied → collect override (only fields that differ from the template)
        const overrides = {};
        const thr = row.querySelector('.sde-ov-threshold').value.trim();
        if (thr !== '') { if (!isFinite(Number(thr))) error = error || 'Override threshold must be a number.'; else overrides.threshold = Number(thr); }
        const aw = row.querySelector('.sde-ov-active').value;
        if (aw === 'below' || aw === 'atOrAbove') overrides.activeWhen = aw;
        if (Object.keys(overrides).length) bindings.push({ ruleId, overrides });
        else if (!t?.isDefault) bindings.push({ ruleId });     // selected a plain template
        // default + no override → no binding (it applies naturally)
      });
      slotEl.querySelectorAll('.sde-inline-row').forEach(ir => {
        const inline = inlineRead(ir);
        if (inline?.error) error = error || inline.error;
        else if (inline?.rule) bindings.push({ inline: inline.rule });
      });
      if (bindings.length) built[slotId] = bindings;
    });
    if (error) { window.showToast(error, 'warning'); return; }
    btn.disabled = true;
    try {
      await updateAsset(assetUUID, { slotRules: built });
      asset.slotRules = built;
      const n = Object.values(built).reduce((a, b) => a + b.length, 0);
      container.querySelector('.sde-badge').innerHTML = headBadge(n);
      container.querySelector('.sde-card')?.classList.toggle('border-primary', n > 0);
      window.showToast('Per-sensor derived rules saved', 'success');
      btn.disabled = false;
    } catch (e) {
      window.showToast('Failed to save sensor rules', 'danger');
      btn.disabled = false;
    }
  });
}

// ── Inline (slot-only) custom rule row ───────────────────────────────────────
function inlineRow(r = {}) {
  return `
    <div class="border rounded p-2 mb-1 bg-body-tertiary sde-inline-row">
      <div class="d-flex align-items-center gap-2 mb-1"><span class="fw-semibold small">Custom rule (this sensor only)</span>
        <button type="button" class="btn btn-sm btn-outline-danger ms-auto p-0 px-1 sde-inline-del"><i class="fa-solid fa-trash"></i></button></div>
      <div class="row g-2" style="max-width:640px;">
        <div class="col-6 col-md-3"><label class="form-label small mb-0">Name</label><input class="form-control form-control-sm si-label" value="${esc(r.label || '')}" placeholder="Load state"></div>
        <div class="col-6 col-md-3"><label class="form-label small mb-0">Key</label><input class="form-control form-control-sm si-key" value="${esc(r.derivedKey || '')}" placeholder="loadState"></div>
        <div class="col-6 col-md-3"><label class="form-label small mb-0">Reads</label>
          <select class="form-select form-select-sm si-metric">${SOURCE_METRICS.map(m => `<option value="${m.key}" ${m.key === r.sourceMetric ? 'selected' : ''}>${esc(m.label)}</option>`).join('')}</select></div>
        <div class="col-6 col-md-3"><label class="form-label small mb-0">Threshold</label><input type="number" step="any" class="form-control form-control-sm si-threshold" value="${r.threshold != null ? esc(r.threshold) : ''}" placeholder="0.5"></div>
        <div class="col-6 col-md-3"><label class="form-label small mb-0">Active when</label>
          <select class="form-select form-select-sm si-active"><option value="atOrAbove" ${r.activeWhen !== 'below' ? 'selected' : ''}>at or above</option><option value="below" ${r.activeWhen === 'below' ? 'selected' : ''}>below</option></select></div>
        <div class="col-6 col-md-3"><label class="form-label small mb-0">Active label</label><input class="form-control form-control-sm si-la" value="${esc(r.labelActive || '')}" placeholder="loaded"></div>
        <div class="col-6 col-md-3"><label class="form-label small mb-0">Inactive label</label><input class="form-control form-control-sm si-li" value="${esc(r.labelInactive || '')}" placeholder="empty"></div>
      </div>
    </div>`;
}

function inlineRead(el) {
  const key = el.querySelector('.si-key').value.trim();
  const thr = el.querySelector('.si-threshold').value.trim();
  if (!key && thr === '') return null;             // empty card → skip
  if (!/^[a-zA-Z][a-zA-Z0-9_]{0,39}$/.test(key)) return { error: 'Custom rule needs a valid Key.' };
  if (thr === '' || !isFinite(Number(thr))) return { error: `Custom rule "${key}" needs a numeric threshold.` };
  return { rule: {
    derivedKey: key,
    label: el.querySelector('.si-label').value.trim() || key,
    sourceMetric: el.querySelector('.si-metric').value,
    threshold: Number(thr),
    activeWhen: el.querySelector('.si-active').value === 'below' ? 'below' : 'atOrAbove',
    labelActive: el.querySelector('.si-la').value.trim() || 'active',
    labelInactive: el.querySelector('.si-li').value.trim() || 'inactive',
  } };
}
